import classNames from 'classnames'

export interface RadioCardOption<T extends string> {
  value: T
  label: string
  description: string
}

interface RadioCardGroupProps<T extends string> {
  name: string
  options: RadioCardOption<T>[]
  value: T
  readOnly?: boolean
  onChange: (value: T) => void
}

export const RadioCardGroup = <T extends string>({ name, options, value, readOnly = false, onChange }: RadioCardGroupProps<T>) => (
  <div className='d-flex flex-wrap gap-2'>
    {options.map((option) => {
      const isSelected = option.value === value
      if (readOnly) {
        return (
          <div
            key={option.value}
            className={classNames(
              'd-flex align-items-start gap-2 p-3 rounded border flex-1',
              isSelected ? 'zocks-border-color-gray-300' : 'zocks-border-color-gray-200',
            )}
          >
            <div className='d-flex flex-column zocks-gap-0.25 flex-grow-1'>
              <span className={classNames('bold', isSelected ? '' : 'zocks-text-gray-500')}>{option.label}</span>
              <span className={classNames('paragraph-small', isSelected ? 'zocks-text-gray-600' : 'zocks-text-gray-500')}>{option.description}</span>
            </div>
            {isSelected ? <i className='bi bi-check-lg zocks-text-gray-900' /> : null}
          </div>
        )
      }
      return (
        <button
          key={option.value}
          type='button'
          className={classNames(
            'd-flex align-items-start gap-2 p-3 rounded text-start flex-1 bg-transparent',
            isSelected ? 'zocks-border-primary border-2 border' : 'border zocks-border-color-gray-200',
          )}
          aria-pressed={isSelected}
          aria-label={`${name}: ${option.label}`}
          onClick={() => onChange(option.value)}
        >
          <div className='d-flex flex-column zocks-gap-0.25 flex-grow-1'>
            <span className='bold'>{option.label}</span>
            <span className='paragraph-small zocks-text-gray-600'>{option.description}</span>
          </div>
          <i className={classNames(isSelected ? 'bi bi-record-circle-fill zocks-text-primary' : 'bi bi-circle zocks-text-gray-500')} />
        </button>
      )
    })}
  </div>
)

interface LabeledRadioCardsProps<T extends string> extends RadioCardGroupProps<T> {
  title: string
  description?: string
}

export const LabeledRadioCards = <T extends string>({ title, description, ...props }: LabeledRadioCardsProps<T>) => (
  <div className='d-flex flex-column zocks-gap-0.5'>
    <div className='d-flex flex-column'>
      <span className='bold'>{title}</span>
      {description ? <span className='paragraph-small zocks-text-gray-600'>{description}</span> : null}
    </div>
    <RadioCardGroup {...props} />
  </div>
)
